import React, { useMemo } from 'react';
import { StyledFormContainer, StyledPaperContainer, StyledSectionHeader, StyledSectionTitle } from '../../PlusWebsiteEditor.styles';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { isSectionFixed, shouldDisableSection } from './HomepageSections.utils';
import HomepageSection from '../../components/HomepageSection/HomepageSection';
import { reorder } from '../../components/DraggableHOC/Draggable.utils';
import { SectionId, WebsiteConfiguration } from '../../types/websiteConfiguration';
import { WebsiteSection as IHomepageSection } from '../../types/sections';
import { ChangeHandler, FieldErrorGetter } from '../../types/handlers';
import { StyledErrorText } from '../../components/ImageUploader/ImageUploader.styles';

type Props = {
  availableSections: IHomepageSection[];
  selectedSections: WebsiteConfiguration['homepage_sections'];
  onChange: ChangeHandler<Array<string>>;
  getError: FieldErrorGetter;
};

const HomepageSections = ({ availableSections, selectedSections = [], onChange, getError }: Props) => {
  const availableHomepageSectionsHash = useMemo(
    () =>
      availableSections.reduce((hash, section) => {
        hash[section._id] = section;
        return hash;
      }, {}),
    [availableSections]
  );

  const isSectionSelectedById = useMemo(
    () =>
      selectedSections.reduce((hash, sectionId) => {
        hash[sectionId] = true;
        return hash;
      }, {}),
    [selectedSections]
  );

  const staticTopSections = availableSections.filter((item) => isSectionFixed(item) && !!isSectionSelectedById[item._id]);
  const draggableSections = selectedSections.map((item) => availableHomepageSectionsHash[item]).filter((item) => !isSectionFixed(item));
  const disabledSections = availableSections.filter((item) => !isSectionSelectedById[item._id]);

  const handleDragEnd = (result: DropResult) => {
    const fixedSections = selectedSections.filter((sectionId) => isSectionFixed(availableHomepageSectionsHash[sectionId]));
    const draggableSections = selectedSections.filter((sectionId) => !isSectionFixed(availableHomepageSectionsHash[sectionId]));

    onChange([...fixedSections, ...reorder(draggableSections, result)], true);
  };

  const handleEnableSwitch = (itemId: SectionId, active: boolean) => {
    if (!active) {
      const idsToRemove = [itemId];

      if (itemId === 'hero-block') {
        idsToRemove.push('learn-more');
      }

      return onChange(
        selectedSections.filter((id) => !idsToRemove.includes(id)),
        true
      );
    }

    const { order } = availableHomepageSectionsHash[itemId];
    const updatedSections =
      typeof order === 'number'
        ? [...selectedSections.slice(0, order), itemId, ...selectedSections.slice(order)]
        : [...selectedSections, itemId];
    onChange(updatedSections, true);
  };

  return (
    <StyledPaperContainer>
      <StyledSectionHeader>
        <StyledSectionTitle>Sections</StyledSectionTitle>
        {getError && <StyledErrorText>{getError()}</StyledErrorText>}
      </StyledSectionHeader>
      <StyledFormContainer>
        <DragDropContext onDragEnd={handleDragEnd}>
          {staticTopSections.map((section) => (
            <HomepageSection
              onSwitch={(active: boolean) => handleEnableSwitch(section._id, active)}
              enabled={!!isSectionSelectedById[section._id]}
              key={section._id}
              item={section}
            />
          ))}
          <Droppable droppableId="homepage-sections">
            {(droppableProvided) => (
              <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {draggableSections.map((section, i) => (
                  <Draggable key={section._id} draggableId={section._id} index={i}>
                    {(draggableProvided, draggableSnapshot) => (
                      <HomepageSection
                        onSwitch={handleEnableSwitch.bind(null, section._id)}
                        enabled={!!isSectionSelectedById[section._id]}
                        provided={draggableProvided}
                        item={section}
                        snapshot={draggableSnapshot}
                        key={section._id}
                      />
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
          {disabledSections.map((section) => (
            <HomepageSection
              onSwitch={handleEnableSwitch.bind(null, section._id)}
              enabled={!!isSectionSelectedById[section._id]}
              disabled={shouldDisableSection(section, selectedSections)}
              key={section._id}
              item={section}
            />
          ))}
        </DragDropContext>
      </StyledFormContainer>
    </StyledPaperContainer>
  );
};

export default HomepageSections;
